/**
 * Content model of the public `/tutorial` page.
 *
 * Long-form documentation lives in typed modules (`tutorial-content.{id,en}.ts`) instead of
 * `messages/parts/*.json`, following the precedent of `src/routes/docs.tsx`: both languages are
 * typed as `TutorialContent`, so TypeScript — not a key-parity script — guarantees that neither
 * language can lose a section. Page chrome (nav label, buttons, meta) still goes through `m.*`.
 *
 * Shell commands are NOT part of this model: they are language-neutral and are defined once in
 * `tutorial-commands.ts`, so a command can never drift between the Indonesian and English page.
 */

export type TutBlock =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'code'; caption?: string; lines: readonly string[] }
  | { kind: 'note'; tone: 'info' | 'warn' | 'ok'; title: string; text: string }
  | { kind: 'table'; head: readonly string[]; rows: readonly (readonly string[])[] }

export type TutStep = {
  /** Short imperative title, e.g. "Tulis idemu". */
  title: string
  blocks: readonly TutBlock[]
}

export type TutSection = {
  /** Overline above the heading (short, uppercase in CSS). */
  overline: string
  title: string
  lead: string
  /** Blocks rendered before the numbered steps. */
  intro?: readonly TutBlock[]
  steps?: readonly TutStep[]
  /** Blocks rendered after the numbered steps. */
  outro?: readonly TutBlock[]
  /** Honest limits of this path — always rendered last, never omitted in one language only. */
  limits?: { title: string; items: readonly string[] }
}

/** Section ids double as anchors (`/tutorial#mandiri`) and as TOC entries. */
export const SECTION_IDS = [
  'pilih',
  'dasar',
  'rencana',
  'bedah',
  'otomatis',
  'mandiri',
  'komputer',
  'team-build',
  'tim',
  'kendali',
  'mcp',
  'kolaborasi',
  'terbitkan',
  'paket',
  'masalah',
  'referensi',
] as const

export type SectionId = (typeof SECTION_IDS)[number]

/** One of the three big cards in the path picker. */
export type TutPath = {
  id: SectionId
  badge: string
  title: string
  who: string
  bullets: readonly string[]
}

export type TutorialContent = {
  hero: { overline: string; title: string; lead: string; meta: string }
  /** Primary paths and additional modes in the `#pilih` section. */
  paths: readonly TutPath[]
  extras: readonly { id: SectionId; title: string; text: string }[]
  tocTitle: string
  /** Every section, keyed by id — TypeScript refuses a language that forgets one. */
  sections: Record<SectionId, TutSection>
  closing: { title: string; lead: string; primary: string; secondary: string }
}
